// @ts-nocheck
import translate from "@insite/client-framework/Translate";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import {
    createCustomerPoNumber,
    CustomerPoNumber,
    deleteCustomerPoNumber,
    getCustomerPoNumberByCustomerID,
    NewPoNumber,
    updateCustomerPoNumber,
} from "@store/Framework/Services/CustomerPoNumberService";
import useGetShipToAndBillTo from "@store/Framework/Store/Data/Accounts/Hooks/useGetShipToAndBillTo";
import { useEffect, useState } from "react";

export interface PoValidationErrors {
    startDate: string[];
    endDate: string[];
}
export type CustomerType = "billTo" | "shipTo";
const useManageDefaultPO = () => {
    const { addToast } = useToaster();
    const [loading, setLoading] = useState(false);
    const [billToPos, setBillToPos] = useState<CustomerPoNumber[]>([]);
    const [shipToPos, setShipToPos] = useState<CustomerPoNumber[]>([]);
    const [selectedOverride, setSelectedOverride] = useState<CustomerType>();
    const { billTo, shipTo } = useGetShipToAndBillTo();
    useEffect(() => {
        const getCustomerPoNumbers = async () => {
            setLoading(true);
            const [billToPos, shipToPos] = await Promise.all([
                getCustomerPoNumberByCustomerID(billTo.id),
                getCustomerPoNumberByCustomerID(shipTo.id),
            ]);
            setBillToPos(billToPos);
            setShipToPos(shipToPos);
            setLoading(false);
            if (shipToPos.length) {
                setSelectedOverride("shipTo");
            } else {
                setSelectedOverride("billTo");
            }
        };
        if (billTo) {
            getCustomerPoNumbers();
        }
    }, [billTo, shipTo]);
    const deletePoNumber = async (poId: string, customerType: CustomerType) => {
        setLoading(true);
        try {
            await deleteCustomerPoNumber(poId);
            if (customerType === "billTo") {
                setBillToPos(billToPos.filter(p => p.id !== poId));
            } else {
                setShipToPos(shipToPos.filter(p => p.id !== poId));
            }

            setLoading(false);
            addToast({ body: translate("PO Number Deleted"), messageType: "success" });
        } catch {
            setLoading(false);
            addToast({ body: translate("Something went wrong"), messageType: "danger" });
        }
    };
    const updatePoNumber = async (po: CustomerPoNumber, customerType: CustomerType) => {
        try {
            setLoading(true);
            const updatedPo = await updateCustomerPoNumber(po);
            if (customerType === "billTo") {
                setBillToPos(billToPos.map(po => (po.id === updatedPo.id ? updatedPo : po)));
            } else {
                setShipToPos(shipToPos.map(po => (po.id === updatedPo.id ? updatedPo : po)));
            }
            setLoading(false);
            addToast({ body: translate("PO Number Updated"), messageType: "success" });
        } catch {
            setLoading(false);
            addToast({ body: translate("Something went wrong"), messageType: "danger" });
        }
    };

    const createPoNumber = async (po: NewPoNumber, customerType: CustomerType) => {
        setLoading(true);
        try {
            const createdPo = await createCustomerPoNumber(po);
            if (customerType === "billTo") {
                setBillToPos([...billToPos, createdPo]);
            } else {
                setShipToPos([...shipToPos, createdPo]);
            }
            setLoading(false);
            addToast({ body: translate("PO Number Created"), messageType: "success" });
        } catch {
            setLoading(false);
            addToast({ body: translate("Something went wrong"), messageType: "danger" });
        }
    };

    const validateStartDate = (startDate: Date, endDate: Date | null, customerType: CustomerType, id: string) => {
        if (startDate.toDateString() === endDate?.toDateString()) {
            return false;
        }

        const existingPos = (customerType === "billTo" ? billToPos : shipToPos).filter(po => po.id !== id);
        if (!existingPos.length) {
            return true;
        }

        const startDatesBeforeNewStartDate = existingPos.filter(po => po.startDate <= startDate);
        const isValid = startDatesBeforeNewStartDate.every(po => {
            if (!po.endDate) {
                return false;
            }
            if (po.endDate.toDateString() === startDate.toDateString()) {
                return false;
            }
            return po.endDate < startDate;
        });
        return isValid;
    };

    const validateEndDate = (startDate: Date, endDate: Date | null, customerType: CustomerType, id: string) => {
        const existingPos = (customerType === "billTo" ? billToPos : shipToPos).filter(po => po.id !== id);
        if (!existingPos.length) {
            return true;
        }
        if (endDate) {
            if (startDate.toDateString() === endDate?.toDateString()) {
                return false;
            }
            const endDatesAfterNewEndDate = existingPos.filter(po => !po.endDate || po.endDate > endDate);
            const endDatesBeforeNewEndDate = existingPos.filter(po => !po.endDate || po.endDate < endDate);
            const isNotBetweenStartAndEnd = endDatesAfterNewEndDate.every(po => {
                return po.startDate > endDate;
            });
            if (!isNotBetweenStartAndEnd) {
                return false;
            }
            const existingPoIsNotBetweenStartAndEnd = endDatesBeforeNewEndDate.every(po => {
                return po.endDate! <= startDate;
            });
            if (!existingPoIsNotBetweenStartAndEnd) {
                return false;
            }

            const startDateDoesNotEqualEndDate = existingPos.every(po => {
                // equality check doesn't work on date objects so we need to convert to string
                return po.startDate.toDateString() !== endDate.toDateString();
            });
            if (!startDateDoesNotEqualEndDate) {
                return false;
            }
        } else {
            const isOnlyPoWithoutEndDate = existingPos.every(po => po.endDate);
            if (!isOnlyPoWithoutEndDate) {
                return false;
            }
            const isStartDateBeforeEveryEndDate = existingPos.every(po => {
                return po.endDate! > startDate;
            });
            if (isStartDateBeforeEveryEndDate) {
                return false;
            }
        }

        return true;
    };
    return {
        selectedOverride,
        setSelectedOverride,
        validateStartDate,
        validateEndDate,
        createPoNumber,
        updatePoNumber,
        deletePoNumber,
        loading,
        billToPos,
        shipToPos,
        billTo,
        shipTo,
    };
};

export default useManageDefaultPO;
